import Alert from '@mui/material/Alert'
import Stack from '@mui/material/Stack'
import { Role } from 'context/authentication/Role.types'
import { TripDetailsContext } from 'context/trips/TripDetailsContext'
import { hasAccess } from 'global/helpers/authorization'
import useDialog from 'global/hooks/useDialog'
import { Fragment, type ReactElement, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import StatusUpdateDialog from 'screens/StatusManager/StatusUpdateDialog'
import StopStatusChip from 'screens/StatusManager/StopStatusChip'
import PrimaryActions from 'screens/TripDetails/PrimaryActions'
import StopType from 'screens/TripDetails/StopType'
import type { CustomsStopProps } from './StopsView.types'
import LegHeader from 'screens/TripDetails/LegHeader'
import type { KNDataCardAction } from 'screens/TripDetails/TripDetails.types'

const CustomsStop = ({
  trip,
  leg,
  stop,
  weblinkToken,
}: CustomsStopProps): ReactElement => {
  const { t } = useTranslation()
  const [tripDetailsState] = useContext(TripDetailsContext)
  const stopErrors = tripDetailsState.stopsErrors?.filter((error) => error['waypointCid'] === stop.wayPointCid)

  const statusUpdateDialog = useDialog<typeof StatusUpdateDialog>()

  const actions: KNDataCardAction[] = []
  if (stop.availableStatuses.length > 0) {
    actions.push({
      label: t('screens.cs.trip_details.card.actions.update_status'),
      variant: 'outlined',
      color: 'success',
      handler: statusUpdateDialog.handleOpen,
    })
  }

  return (
    <>
      <Stack spacing={2}>
        {stopErrors.length > 0 && (
          <Alert severity="error">
            {stopErrors.map((error, index) => (
              <Fragment key={error.message}>
                {index > 0 && <br />}
                {error.message}
              </Fragment>
            ))}
          </Alert>
        )}

        <Stack spacing={1}>
          <Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {stop.statuses[0] && (
                <StopStatusChip statusId={stop.statuses[0].id} />
              )}
              <StopType stop={stop} />
            </Stack>
            <LegHeader trip={trip} leg={leg} size="large" />
          </Stack>
        </Stack>

        {actions.length > 0 && (hasAccess(Role.Editor) || weblinkToken) && (
          <PrimaryActions actions={actions} />
        )}
      </Stack>

      {stop.availableStatuses.length > 0 && (
        <StatusUpdateDialog
          payload={{
            trip,
            leg,
            stop,
            weblinkToken,
          }}
          open={statusUpdateDialog.isOpen}
          onAction={statusUpdateDialog.handleAction}
          onClose={statusUpdateDialog.handleClose}
        />
      )}
    </>
  )
}

export default CustomsStop
